import React from 'react';
import Link from 'next/link';
import Image from 'next/image';

const IntegrationsSection = () => {
  // Integration partners data
  const integrations = [
    {
      name: 'Zapier',
      icon: '/images/icons/Zapier.png',
      description: 'Connect Personalia to 5,000+ apps without coding',
      url: 'https://zapier.com/apps/personalia/integrations',
      cta: 'Explore'
    },
    {
      name: 'Make',
      icon: '/images/icons/Make.png',
      description: 'Create complex automations with visual workflows',
      url: '/contact',
      cta: 'Contact Us'
    },
    {
      name: 'n8n',
      icon: '/images/icons/n8n.png',
      description: 'Open-source workflow automation with self-hosting options',
      url: 'https://www.npmjs.com/package/n8n-nodes-personalia',
      cta: 'Explore'
    },
    {
      name: 'WordPress',
      icon: '/images/icons/WordPress.png',
      description: 'Integrate with WordPress and Elementor for dynamic websites',
      url: '/contact',
      cta: 'Contact Us'
    },
    {
      name: 'Enfocus Switch',
      icon: '/images/icons/Enfocus Switch.png',
      description: 'Integrate with Enfocus Switch for dynamic content',
      url: 'https://www0.enfocus.com/en/appstore/product/personalia',
      cta: 'Explore'
    }
  ];

  return (  
    <section className="section bg-gray-50" id="integrations">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Connect With Your Tools</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Personalia integrates seamlessly with your favorite platforms and automation tools.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
          {integrations.map((integration, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow flex flex-col h-full">
              <div className="flex-grow">
                <div className="flex justify-center mb-4">
                  <Image src={integration.icon} alt={`${integration.name} Icon`} width={64} height={64} />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-center">{integration.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{integration.description}</p>
              </div>
              <div className="mt-auto text-center">
                <a 
                  href={integration.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 text-sm font-medium text-primary hover:text-primary-dark border border-primary rounded-md hover:bg-primary-50 transition-colors w-full"
                >
                  {integration.cta}
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-white p-6 md:p-8 rounded-lg shadow-md border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div>
              <h3 className="text-2xl font-bold mb-4">1. Request PDF Generation</h3>
              <p className="text-gray-600 mb-6">
                Start by sending a POST request to generate a PDF document with your template and data.
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-gray-300 text-sm">
                  <code>{"// 1. Request PDF generation\n"}
{"const response = await fetch(\n"}
{"  'https://api.personalia.io/v1/content',\n"}
{"  {\n"}
{"    method: 'POST',\n"}
{"    headers: {\n"}
{"      'Content-Type': 'application/json',\n"}
{"      'Authorization': 'ApiKey YOUR_API_KEY'\n"}
{"    },\n"}
{"    body: JSON.stringify({\n"}
{"      TemplateId: 'c790fd3b-10ef-4a58-963b-b688e564eefa',\n"}
{"      Fields: {\n"}
{"        'First Name': 'John',\n"}
{"        'Machine': 'AK-2000',\n"}
{"        'Color': 'Blue'\n"}
{"      },\n"}
{"      Output: {\n"}
{"        Format: 'PDF',\n"}
{"        Quality: 'Display',\n"}
{"        Package: false,\n"}
{"        StrictPolicy: true\n"}
{"      }\n"}
{"    })\n"}
{"  }\n"}
{");\n\n"}
{"// 2. Get the request ID for polling\n"}
{"const { RequestId } = await response.json();"}</code>
                </pre>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4">2. Poll for Completion</h3>
              <p className="text-gray-600 mb-6">
                Poll the API to check if your PDF is ready for download. Once it is ready the URL to the asset remains live for 30 minutes. 
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-gray-300 text-sm">
                  <code>{"// 3. Poll for completion\n"}
{"const getResult = async () => {\n"}
{"  const result = await fetch(\n"}
{"    `https://api.personalia.io/v1/content?requestId=${RequestId}`,\n"}
{"    { \n"}
{"      headers: { \n"}
{"        'Authorization': 'ApiKey YOUR_API_KEY' \n"}
{"      } \n"}
{"    }\n"}
{"  );\n"}
{"  return await result.json();\n"}
{"};\n\n"}
{"// 4. Check status periodically\n"}
{"let result = await getResult();\n"}
{"while (result.Status === 'InProgress') {\n"}
{"  await new Promise(resolve => setTimeout(resolve, 1000));\n"}
{"  result = await getResult();\n"}
{"}\n\n"}
{"// 5. Handle the result\n"}
{"if (result.Status === 'Completed') {\n"}
{"  console.log('Download URL:', result.URLs[0]);\n"}
{"  // URL is valid for 15 minutes\n"}
{"} else if (result.Status === 'Failed') {\n"}
{"  console.error('Generation failed:', result.FailureDescription);\n"}
{"}"}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-white p-6 md:p-8 rounded-lg shadow-md border border-gray-100">
          <h3 className="text-2xl font-bold mb-4">API Endpoint Comparison</h3>
          <p className="text-gray-600 mb-6">
            Personalia offers two different approaches to content generation through our API, each suited for different use cases.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h4 className="text-xl font-semibold text-primary mb-3">Immediate Rendering</h4>
              <p className="text-gray-700 mb-4">
                <span className="font-medium">Endpoints:</span> POST /v1/content + GET /v1/content
              </p>
              <p className="text-gray-600 mb-4">
                The POST request triggers immediate rendering of your asset, and you poll the GET endpoint until the asset is ready for download. 
                Once complete, the download URL remains valid for 30 minutes.
              </p>
              <h5 className="font-semibold mb-2">Best for:</h5>
              <ul className="list-disc pl-5 text-gray-600 space-y-1">
                <li>Immediate content needs</li>
                <li>Batch processing</li>
                <li>Server-side applications</li>
                <li>When you need the asset right away</li>
              </ul>
              <p className="text-red-600 mt-7">This method is best when you need the asset immediately. Credits are charged immediately</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h4 className="text-xl font-semibold text-primary mb-3">On-Demand Rendering</h4>
              <p className="text-gray-700 mb-4">
                <span className="font-medium">Endpoint:</span> POST /v1/content/url
              </p>
              <p className="text-gray-600 mb-4">
                This endpoint immediately returns a URL that stays valid for 30 days. The asset is only rendered and delivered when someone accesses the URL, 
                and the final document is returned directly to the user.
              </p>
              <h5 className="font-semibold mb-2">Best for:</h5>
              <ul className="list-disc pl-5 text-gray-600 space-y-1">
                <li>Email campaigns with document links</li>
                <li>Cost optimization (only pay when rendered)</li>
                <li>User-triggered document generation</li>
                <li>When immediate rendering isn't needed</li>
              </ul>
              <p className="text-red-600 mt-7">This method is best for when you want to generate content on demand and only pay for credits when the asset is rendered. </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationsSection;
